import React from 'react';
import axios from 'axios';
import { Link, useNavigate } from "react-router-dom";

import Logo from '../Components/Logo';
import Loading from '../Components/Loading';
import WarningPanel from '../Components/WarningPanel';
import LegalNotice from '../Components/Legal/LegalNotice';
import UnavailableSystem from '../Components/UnavailableSystem';

import styles from '../styles/Auth.module.css';

import IconContainer from '../Components/IconContainer';
import { ReactComponent as XIcon } from '../icons/x-close.svg';
import { ReactComponent as ArrowLeftIcon } from '../icons/arrow-left.svg';

export default function Registration() {
    const navigate = useNavigate();
    const inputsRef = React.useRef([]);

    const [content, setContent] = React.useState('firstStep');

    const [email, setEmail] = React.useState('');
    const [code, setCode] = React.useState(Array(6).fill(''));
    const [lastname, setLastname] = React.useState('');
    const [firstname, setFirstname] = React.useState('');
    const [isNoSurname, setIsNoSurname] = React.useState(false);
    const [surname, setSurname] = React.useState('');
    const [gender, setGender] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [passwordConfirm, setPasswordConfirm] = React.useState('');
    const [status, setStatus] = React.useState('');
  
    const [dbStatus, setDbStatus] = React.useState('checking');
    const [loading, setLoading] = React.useState(true);
    const [loadingButton, setLoadingButton] = React.useState(false);

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get('/api/health');
                if (response.status === 200) {
                    setDbStatus('connected');
                }
            } catch (err) {
                setDbStatus('Отсутствует подключение к серверу');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const [errorsForm, setErrorsForm] = React.useState({});

    const validateEmail = () => {
        const newErrors = {};

        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = 'Введите корректный адрес электронной почты';
        }

        setErrorsForm(newErrors);
        return Object.keys(newErrors).length === 0;
    };
  
    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setLoadingButton(true);

        if (validateEmail()) {
            axios.post('/api/send-verification-code', { 
                email 
            })
            .then(() => {
                setContent('secondStep');
            })
            .catch((error) => {
                console.error(error);
                setStatus(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
            })
            .finally(() => {
                setLoadingButton(false);
                setTimeout(() => {
                    setStatus('');
                }, 4000);
            });
        } else {
            setLoadingButton(false);
        }
    };
  
    const handleCodeSubmit = async (e) => {
        e.preventDefault();
        setLoadingButton(true);

        if (email && code) {
            axios.post('/api/verify-code', { 
                email, 
                verificationCode: code.join(''),
            }).then(() => {
                setContent('thirdStep');
            }).catch((error) => {
                console.error(error);
                setStatus(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
            })
            .finally(() => {
                setLoadingButton(false);
                setTimeout(() => {
                    setStatus('');
                }, 2500);
            });
        } else {
            setStatus('Вы не ввели обязательные поля.');
            setTimeout(() => {
                setStatus('');
            }, 2500);
            setLoadingButton(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!lastname || lastname.length < 2 || lastname.length > 35) {
            newErrors.lastname = 'Значение не может быть меньше 2 и более 35 символов';
        }
        if (!firstname || firstname.length < 2 || firstname.length > 20) {
            newErrors.firstname = 'Значение не может быть меньше 2 и более 20 символов';
        }
        if (!isNoSurname && (surname.length < 2 || surname.length > 20)) {
            newErrors.surname = 'Значение не может быть меньше 2 и более 20 символов';
        }
        if (!gender) {
            newErrors.gender = 'Выберите пол';
        }
        if (!password || password.length > 30) {
            newErrors.password = 'Значение не может быть пустым и более 30 символов';
        }
        if (passwordConfirm !== password) {
            newErrors.passwordConfirm = 'Введенные пароли не совпадают';
        }

        setErrorsForm(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleRegistrationSubmit = (e) => {
        e.preventDefault();
        setLoadingButton(true);

        if (validateForm()) {
            axios.post('/api/registration', {
                email,
                lastname,
                firstname,
                surname,
                gender,
                password,
                passwordConfirm,
            })
            .then(response => {
                if (response.data.token) {
                    localStorage.setItem('token', response.data.token);
                    window.location.href = '/';
                } else {
                    setContent('fourthStep');
                    setStatus(response.data.message);
                }
            })
            .catch((error) => {
                console.error(error);
                setStatus(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
            })
            .finally(() => {
                setLoadingButton(false);
                setTimeout(() => {
                    setStatus('');
                }, 4000);
            })
        } else {
            setLoadingButton(false);
        }
    };

    const handleChange = (value, index) => {
        if (!/^\d*$/.test(value)) return;
        const newCode = [...code];
        newCode[index] = value.slice(-1);
        setCode(newCode);
        if (value && index < 5) inputsRef.current[index + 1].focus();
        };

        const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputsRef.current[index - 1].focus();
        }
    };

    const handlePaste = (e) => {
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (!pasted) return;
        const newCode = Array(6).fill('');
        pasted.split('').forEach((char, i) => newCode[i] = char);
        setCode(newCode);
        inputsRef.current[Math.min(pasted.length, 5)].focus();
        e.preventDefault();
    };


    return (
        <>
        {loading ? (
            <Loading />
        ) : dbStatus === 'connected' ? (
            <>
                <div className={styles.indexMain}>
                    <div className={styles.lining}>
                        <div className={styles.leftSide}>
                            {content == 'firstStep' && 
                            <>
                                <span>
                                    <div style={{width: '100%', display: 'flex', justifyContent: 'space-between'}}>
                                        <Logo to='/' position='relative' />
                                        <div className={styles.closeBtnContainerMobile}>
                                            <IconContainer Icon={XIcon} onClick={() => navigate('/')} size = '20' color = 'black' className = '' />
                                        </div>
                                    </div>
                                    <h1>Регистрация Личного кабинета арбитражного управляющего</h1>
                                    <a className={styles.labelInput}>Введите адрес электронной почты</a>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type='email'
                                            value={email}
                                            onChange={(e) => {setEmail(e.target.value)}}
                                            className={errorsForm.email ? 'inputError' : ''}
                                            placeholder='Введите email'
                                            minLength='3'
                                            maxLength='100'
                                            required
                                        />
                                        {errorsForm.email && <div className='errorUnderInput'>{errorsForm.email}</div>}
                                    </div>
                                    <p className={styles.infoBlock} style={{margin: '10px 0'}}>Введите рабочий email. На него будут приходить важные уведомления</p>
                                </span>
                                <span>
                                    {status && <WarningPanel type='error' text={status} />}
                                    {loadingButton ? 
                                        <Loading /> 
                                        : 
                                        <button className='button maxWidth' onClick={handleEmailSubmit}>Зарегистрироваться</button>
                                    }
                                    <LegalNotice />
                                </span>
                            </>
                            }
                            {content == 'secondStep' &&
                            <>
                                <span>
                                    <div style={{width: '100%', display: 'flex', justifyContent: 'space-between'}}>
                                        <Logo to='/' position='relative' />
                                        <div className={styles.closeBtnContainerMobile}>
                                            <IconContainer Icon={XIcon} onClick={() => navigate('/')} size = '20' color = 'black' className = '' />
                                        </div>
                                    </div>
                                    <div className={styles.headline}>
                                        <h1>Введите код подтверждения</h1>
                                    </div>
                                    <a className={styles.labelInput} style={{color: '#919191', marginBottom: '10px'}}>Отправили 6-значный код {email ?? 'на '+ email }</a>
                                    <div style={{ display: 'flex', gap: '3px' }}>
                                    {code.map((digit, index) => (
                                        <input
                                        key={index}
                                        ref={el => inputsRef.current[index] = el}
                                        type='text'
                                        inputMode='numeric'
                                        value={digit}
                                        onChange={(e) => handleChange(e.target.value, index)}
                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                        onPaste={handlePaste}
                                        maxLength={1}
                                        style={{ height: '40px', textAlign: 'center' }}
                                        />
                                    ))}
                                    </div>
                                    <p className={styles.infoBlock} style={{margin: '10px 0'}}>Если письмо не пришло во «Входящие», проверьте другие папки почтового клиента — «Спам», «Промоакции», «Рассылки» или «Все письма» (список зависит от вашего почтового сервиса). Если письмо найдётся там, нажмите «Не спам!», чтобы следующие письма от системы сразу попадали во «Входящие»</p>
                                </span>
                                <span>
                                    {status && <WarningPanel type='error' text={status} />}
                                    {loadingButton ? 
                                        <Loading /> 
                                        : 
                                        <span style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                                            <button className='button maxWidth optionalButton' onClick={() => {setContent('firstStep'); setCode(Array(6).fill('')); setEmail('');}}>
                                                <svg viewBox="0 0 24 24">
                                                    <path d="M17,11H9.41l3.3-3.29a1,1,0,1,0-1.42-1.42l-5,5a1,1,0,0,0-.21.33,1,1,0,0,0,0,.76,1,1,0,0,0,.21.33l5,5a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L9.41,13H17a1,1,0,0,0,0-2Z"></path>
                                                </svg>
                                                Назад
                                            </button>
                                            <button className='button maxWidth' onClick={handleCodeSubmit}>Подтвердить почту</button>
                                        </span>
                                    }
                                    <LegalNotice />
                                </span>
                            </>
                            }
                            {content == 'thirdStep' && 
                            <>
                                <span>
                                    <div style={{width: '100%', display: 'flex', justifyContent: 'space-between'}}>
                                        <Logo to='/' position='relative' />
                                        <div className={styles.closeBtnContainerMobile}>
                                            <IconContainer Icon={XIcon} onClick={() => navigate('/')} size = '20' color = 'black' className = '' />
                                        </div>
                                    </div>
                                    <div className={styles.headline}>
                                        <h1>Последний шаг</h1>
                                    </div>

                                    <a className={styles.labelInput}>Введите фамилию</a>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type='text'
                                            value={lastname}
                                            onChange={(e) => setLastname(e.target.value)}
                                            className={errorsForm.lastname ? 'inputError' : ''}
                                            placeholder='Введите фамилию'
                                            minLength='3'
                                            maxLength='100'
                                            required
                                        />
                                        {errorsForm.lastname && <div className='errorUnderInput'>{errorsForm.lastname}</div>}
                                    </div>

                                    <a className={styles.labelInput}>Введите имя</a>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type='text'
                                            value={firstname}
                                            onChange={(e) => setFirstname(e.target.value)}
                                            className={errorsForm.firstname ? 'inputError' : ''}
                                            placeholder='Введите имя'
                                            minLength='4'
                                            maxLength='180'
                                            required
                                        />
                                        {errorsForm.firstname && <div className='errorUnderInput'>{errorsForm.firstname}</div>}
                                    </div>

                                    <a className={styles.labelInput}>Введите отчество</a>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type='text'
                                            value={surname}
                                            onChange={(e) => setSurname(e.target.value)}
                                            className={errorsForm.surname ? 'inputError' : ''}
                                            placeholder='Введите отчество'
                                            maxLength='180'
                                            disabled={isNoSurname}
                                        />
                                        {errorsForm.surname && <div className='errorUnderInput'>{errorsForm.surname}</div>}
                                        <label className='legCheckboxWrapper'>
                                            <input 
                                            type='checkbox'
                                            checked={isNoSurname}
                                            onChange={() => setIsNoSurname(!isNoSurname)} />
                                            <a>Отчество отсутствует</a>
                                        </label>
                                    </div>

                                    <a className={styles.labelInput}>Укажите пол</a>
                                    <div className={styles.inputWrapper}>
                                        <div className='radioSwitchWrapper'>
                                            <label className={`radioSwitchLabel ${gender === 'Муж' ? 'selected' : ''}`}>
                                                <input
                                                    type='radio'
                                                    name='typeSelector'
                                                    value='Муж'
                                                    checked={gender === 'Муж'}
                                                    onChange={(e) => setGender(e.target.value)}
                                                />
                                                <span>Мужской</span>
                                            </label>
                                            <label className={`radioSwitchLabel ${gender === 'Жен' ? 'selected' : ''}`}>
                                                <input
                                                    type='radio'
                                                    name='typeSelector'
                                                    value='Жен'
                                                    checked={gender === 'Жен'}
                                                    onChange={(e) => setGender(e.target.value)}
                                                />
                                                <span>Женский</span>
                                            </label>
                                        </div>
                                        {errorsForm.gender && <div className='errorUnderInput'>{errorsForm.gender}</div>}
                                    </div>

                                    <a className={styles.labelInput}>Придумайте пароль</a>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type='text'
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className={errorsForm.password ? 'inputError' : ''}
                                            placeholder='Введите пароль'
                                            minLength='4'
                                            maxLength='180'
                                            required
                                        />
                                        {errorsForm.password && <div className='errorUnderInput'>{errorsForm.password}</div>}
                                    </div>

                                    <a className={styles.labelInput}>Повторите пароль</a>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type='text'
                                            value={passwordConfirm}
                                            onChange={(e) => setPasswordConfirm(e.target.value)}
                                            className={errorsForm.passwordConfirm ? 'inputError' : ''}
                                            placeholder='Введите пароль повторно'
                                            minLength='4'
                                            maxLength='180'
                                            required
                                        />
                                        {errorsForm.passwordConfirm && <div className='errorUnderInput'>{errorsForm.passwordConfirm}</div>}
                                    </div>
                                </span>
                                <span>
                                    {status && <WarningPanel type='error' text={status} />}
                                    {loadingButton ? 
                                        <Loading /> 
                                        : 
                                        <span style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                                            <button className='button maxWidth optionalButton' onClick={() => {setContent('firstStep'); setCode(Array(6).fill('')); setEmail('');}}>
                                                <svg viewBox="0 0 24 24">
                                                    <path d="M17,11H9.41l3.3-3.29a1,1,0,1,0-1.42-1.42l-5,5a1,1,0,0,0-.21.33,1,1,0,0,0,0,.76,1,1,0,0,0,.21.33l5,5a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L9.41,13H17a1,1,0,0,0,0-2Z"></path>
                                                </svg>
                                                Использовать другой email
                                            </button>
                                            <button className='button maxWidth' onClick={handleRegistrationSubmit}>Завершить регистрацию</button>
                                        </span>
                                    }
                                    <LegalNotice />
                                </span>
                            </>
                            }
                            {content == 'fourthStep' &&
                            <>
                                <span>
                                    <div style={{width: '100%', display: 'flex', justifyContent: 'space-between'}}>
                                        <Logo to='/' position='relative' />
                                        <div className={styles.closeBtnContainerMobile}>
                                            <IconContainer Icon={XIcon} onClick={() => navigate('/')} size = '20' color = 'black' className = '' />
                                        </div>
                                    </div>
                                    <h1>Личный кабинет успешно зарегистрирован</h1>
                                    <p className={styles.infoBlock} style={{margin: '10px 0'}}>Вы успешно зарегистрированы в системе.</p>
                                    <p className={styles.infoBlock} style={{margin: '10px 0'}}>После регистрации учётная запись проходит проверку, и доступ к системе может быть предоставлен в течение 3 рабочих дней. Оповещение будет отправлено на ваш адрес электронной почты</p>
                                </span>
                                <span>
                                </span>
                            </>
                            }
                        </div>

                        <div className={styles.rightSide}>
                            <div className={styles.closeBtnContainer}>
                                <IconContainer Icon={XIcon} onClick={() => navigate('/')} size = '20' color = 'black' className = '' />
                            </div>
                            <span>
                                <div className={styles.additionalBtn}>
                                    <img src='/img/icons/signin.svg' alt='Вход' />
                                    <span>
                                        <h2>Уже есть учётная запись?</h2>
                                        <Link to='/auth/login'>Перейти к странице входа</Link>
                                    </span>
                                </div>
                                <div className={styles.additionalBtn}>
                                    <img src='/img/icons/comment-question.svg' alt='Поддержка' />
                                    <span>
                                        <h2>Возникла проблема?</h2>
                                        <Link to='/send-feedback'>Обратиться в поддержку</Link>
                                    </span>
                                </div>
                                <div className={styles.additionalBtn}>
                                    <img src='/img/icons/file-info-alt.svg' alt='Юридическая информация' />
                                    <span>
                                        <h2>Обратите внимание</h2>
                                        <Link to='/legal/privacy-policy'>Пользовательское соглашение и политика конфиденциальности</Link>
                                    </span>
                                </div>
                            </span>
                        </div>
                    </div>
                </div>
            </>
        ) : (
            <UnavailableSystem dbStatus={dbStatus} />
        )}
        </>
    )
};
